import * as XLSX from 'xlsx';

/**
 * 파일 확장자 기반 CSV 여부 판단 (대소문자 무시)
 */
const isCsvFile = (file) => {
    const name = (file?.name || '').toLowerCase();
    return name.endsWith('.csv');
};

const isZipFile = (file) => (file?.name || '').toLowerCase().endsWith('.zip');

/**
 * 파일을 읽어 첫 번째 워크시트를 반환합니다.
 * - CSV : readAsText(file, encoding) 후 XLSX.read(type='string')
 * - XLS/XLSX : readAsArrayBuffer(file) 후 XLSX.read(type='array')
 *
 * @param {File} file
 * @param {string} encoding (예: 'UTF-8', 'EUC-KR')
 * @returns {Promise<XLSX.WorkSheet>}
 */
export const parseFileToSheet = async (file, encoding = 'UTF-8') => {
    if (!file) throw new Error('No file provided');

    if (isZipFile(file)) {
        // Shapefile ZIP은 xlsx 파서 대상이 아님
        throw new Error('ZIP files must be handled via shpUtils, not xlsxUtils.');
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        const csv = isCsvFile(file);

        reader.onload = (e) => {
            try {
                let workbook;
                if (csv) {
                    // CSV 텍스트를 바로 읽어들여 파싱
                    const text = e.target.result;
                    workbook = XLSX.read(text, {
                        type: 'string',
                        cellDates: true,
                        dateNF: 'yyyy-mm-dd',
                        raw: true,
                    });
                } else {
                    // 바이너리(ArrayBuffer) → XLSX 파싱
                    const ab = new Uint8Array(e.target.result);
                    workbook = XLSX.read(ab, { type: 'array' });
                }

                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                resolve(worksheet);
            } catch (err) {
                reject(err);
            }
        };

        reader.onerror = (err) => reject(err);

        if (csv) {
            reader.readAsText(file, encoding);
        } else {
            reader.readAsArrayBuffer(file);
        }
    });
};

/**
 * 워크시트를 Toast UI Grid에 바로 물릴 수 있는 형태로 변환합니다.
 * - header: 1 로 전체를 2차원 배열로 받은 뒤,
 *   첫 행을 headers, 나머지를 데이터로 처리
 * - 반환: { columns, rows, headers }
 *
 * columns: [{ name, header, value, idx, width, sortable, filter, editable }]
 * rows   : [{ id, [header0]: value0, [header1]: value1, ... }]
 *
 * @param {XLSX.WorkSheet} worksheet
 * @returns {{ columns: Array, rows: Array, headers: Array }}
 */
export const sheetToGrid = (worksheet) => {
    const rows2d = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    if (!rows2d || rows2d.length === 0) {
        return { columns: [], rows: [], headers: [] };
    }

    const headers = rows2d[0];
    const dataRows = rows2d.slice(1);

    // 컬럼 정의
    const columns = headers.map((header, idx) => ({
        name: header,
        header: header,
        value: header,
        idx,
        width: 300,
        sortable: true,
        filter: 'text',
        editable: false,
    }));

    // 행 데이터
    const rows = dataRows.map((arr, rIdx) => {
        const obj = { id: rIdx + 1 };
        headers.forEach((key, i) => {
            obj[key] = arr[i] ?? '';
        });
        return obj;
    });

    return { columns, rows, headers };
};

/**
 * 헤더 목록을 보고 주소 컬럼으로 추정되는 컬럼을 찾아 반환합니다.
 * - '주소' 또는 'addr' 키워드가 포함된 컬럼을 우선 반환
 * - 없으면 첫 번째 컬럼 반환
 *
 * @param {Array<{value?: string, header?: string}>} columns
 * @returns {{...} | null}
 */
export const guessAddressField = (columns = []) => {
    if (!columns.length) return null;
    const found = columns.find((c) => {
        const v = String(c.value ?? c.header ?? '').toLowerCase();
        return /주소|addr/.test(v);
    });
    return found || columns[0];
};